// Engineering phases
export type Phase = 'pre_bom' | 'evt' | 'dvt' | 'pvt' | 'mp';

// Part status for TBD tracking
export type PartStatus = 'tbd' | 'preliminary' | 'confirmed' | 'approved';

// Alternate part qualification status
export type AlternateStatus = 'sample' | 'qualified' | 'approved';

// Product represents a BOM product/assembly
export interface Product {
  product_id: string;
  product_code: string;
  product_name: string;
  category: string; // finished_good, assembly, pcba
  current_phase: Phase;
  current_revision: string;
  phase_history: PhaseEntry[];
  status: string; // active, on_hold, obsolete
  project_code?: string;
  target_mp_date?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// PhaseEntry tracks phase transition history
export interface PhaseEntry {
  phase: Phase;
  entered_at: string;
  exited_at?: string;
  revision_at_entry: string;
  approved_by: string;
}

// Revision represents a BOM revision
export interface Revision {
  id: string;
  product_id: string;
  revision: string;
  phase: Phase;
  ecn_number?: string;
  effective_date?: string;
  status: string; // draft, pending_approval, released, superseded
  change_type?: string;
  change_summary?: string;
  validation_status: ValidationStatus;
  planned_build_qty?: number;
  actual_build_qty?: number;
  items: BOMItem[];
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// ValidationStatus tracks phase-specific validations
export interface ValidationStatus {
  functional_test: boolean;
  design_review: boolean;
  dfm_review: boolean;
  process_validation: boolean;
  quality_approval: boolean;
}

// BOMItem represents a single line in a BOM
export interface BOMItem {
  line_no: number;
  level: number;
  parent_line?: number;
  part_code: string;
  description: string;
  part_type?: string;      // e.g., Resistor, Capacitor, IC, Connector
  package_size?: string;   // e.g., 0402, 0603, SOIC-8, QFN-48
  specification?: string;  // e.g., 10K 1% 1/16W, 100nF 16V X5R
  brand?: string;          // e.g., Yageo, Murata, TI
  digikey_pn?: string;     // Digikey part number for pricing lookup
  qty_per: number;
  unit: string;
  ref_designator?: string;
  is_assembly: boolean;
  is_phantom: boolean;
  part_status: PartStatus;
  alternates?: AlternatePart[];
  notes?: string;
}

// AlternatePart represents an alternate part option
export interface AlternatePart {
  part_code: string;
  description: string;
  status: AlternateStatus;
  priority: number;
  notes?: string;
}

// PhaseTransition represents a phase gate record
export interface PhaseTransition {
  id: string;
  product_id: string;
  from_phase: Phase;
  to_phase: Phase;
  from_revision: string;
  to_revision?: string;
  transition_date?: string;
  checklist: TransitionChecklist;
  attachments?: Attachment[];
  notes?: string;
  status: string; // pending, approved, rejected
  requested_by: string;
  requested_at: string;
  approved_by?: string;
  approved_at?: string;
}

// TransitionChecklist contains checklist items for phase transition
export interface TransitionChecklist {
  items: ChecklistItem[];
  all_passed: boolean;
}

// ChecklistItem represents a single checklist item
export interface ChecklistItem {
  item: string;
  required: boolean;
  checked: boolean;
  checked_by?: string;
  checked_at?: string;
  notes?: string;
}

// Attachment represents a file attachment
export interface Attachment {
  name: string;
  url: string;
  type: string; // test_report, issue_log, review_doc
  uploaded_by: string;
  uploaded_at: string;
}

// Build represents a build/prototype record
export interface Build {
  id: string;
  build_id: string;
  product_id: string;
  revision: string;
  phase: Phase;
  build_number: number;
  planned_qty: number;
  actual_qty: number;
  start_date?: string;
  completion_date?: string;
  status: string; // planned, in_progress, completed, on_hold
  location?: string;
  issues?: BuildIssue[];
  yield_percent?: number;
  notes?: string;
  created_by: string;
  created_at: string;
}

// BuildIssue represents an issue found during a build
export interface BuildIssue {
  issue_id: string;
  description: string;
  severity: string; // critical, major, minor
  part_code?: string;
  root_cause?: string;
  resolution?: string;
  resolved_at?: string;
  created_by: string;
  created_at: string;
}

// ECN represents an Engineering Change Notice
export interface ECN {
  ecn_number: string;
  title: string;
  reason: string; // cost_reduction, performance, obsolescence, quality, customer_request
  description: string;
  affected_products: ECNAffectedProduct[];
  status: string; // draft, pending, approved, rejected, implemented
  requested_by: string;
  requested_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  approved_by?: string;
  approved_at?: string;
  target_implementation_date?: string;
  implemented_at?: string;
}

// ECNAffectedProduct represents a product affected by an ECN
export interface ECNAffectedProduct {
  product_id: string;
  from_revision: string;
  to_revision?: string;
  change_type: string; // component_change, qty_change, add, remove, redesign
  change_details?: Record<string, unknown>;
}

// WhereUsedEntry represents where a part is used
export interface WhereUsedEntry {
  product_id: string;
  product_name: string;
  revision: string;
  phase: Phase;
  qty_per: number;
  level: number;
}

// ExplodedItem represents a line in an exploded BOM
export interface ExplodedItem {
  level: number;
  path: string;
  part_code: string;
  description: string;
  qty_per: number;
  extended_qty: number;
  unit: string;
  ref_designator?: string;
  is_assembly: boolean;
}

// CostRollup represents cost breakdown for a product
export interface CostRollup {
  product_id: string;
  revision: string;
  total_cost: number;
  currency: string;
  items: CostItem[];
  cached_at: string;
}

// CostItem represents a single item in a cost rollup
export interface CostItem {
  part_code: string;
  description: string;
  qty: number;
  unit_cost: number;
  extended_cost: number;
}

// ProductPhaseInfo contains product info for phase summary
export interface ProductPhaseInfo {
  product_id: string;
  product_code: string;
  product_name: string;
  current_revision: string;
  tbd_count: number;
}

// Phase display names
export const PhaseDisplayNames: Record<Phase, string> = {
  pre_bom: 'Pre-BOM',
  evt: 'EVT',
  dvt: 'DVT',
  pvt: 'PVT',
  mp: 'Mass Production',
};

// Phase colors for UI
export const PhaseColors: Record<Phase, string> = {
  pre_bom: 'bg-gray-500',
  evt: 'bg-blue-500',
  dvt: 'bg-yellow-500',
  pvt: 'bg-orange-500',
  mp: 'bg-green-500',
};

// Part status display names
export const PartStatusDisplayNames: Record<PartStatus, string> = {
  tbd: 'TBD',
  preliminary: 'Preliminary',
  confirmed: 'Confirmed',
  approved: 'Approved',
};

// Part status colors
export const PartStatusColors: Record<PartStatus, string> = {
  tbd: 'text-red-600 bg-red-100',
  preliminary: 'text-yellow-600 bg-yellow-100',
  confirmed: 'text-blue-600 bg-blue-100',
  approved: 'text-green-600 bg-green-100',
};
